/**
 * 需要想定 (単日) の小売の内訳 (`GetDemandForecastRetailerBreakdown`) の wire → 表示の写像と、
 * グループタブの選択の解決。
 *
 * どの小売がグループ別か・グループとその他の実績は backend が決める。ここは応答の oneof を
 * 網羅的に写し、選んだタブがどの葉 (グループ・その他) かを引くだけで、業務判断を持たない。
 * 「その他」の表示名は呼び出し側が i18n で付ける (`kind: 'residual'`)。
 */
import { DemandBreakdownStatus } from '@ebalance/proto/bffuser/v1/demand_breakdown_mode_model_pb'
import type {
  GetDemandForecastRetailerBreakdownResponse,
  RetailerDraft,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import { formatProtoDate } from '~/pages/DemandForecastDaily/models/sectionHelpers'
import { protoSlotsToKoma } from '~/pages/DemandForecastDaily/models/slotHelpers'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

/** 合計タブ (小売そのもの。今までどおり小売の値を入力・確定する) の key。 */
export const BREAKDOWN_TOTAL_TAB = 'total'

/** グループタブ 1 つ分 (需要グループ 1 つ、またはその他)。 */
export type BreakdownLeafView = {
  /** タブの key。グループは `group:<需要グループ id>`、その他は `residual`。 */
  key: string
  kind: 'group' | 'residual'
  /** グループ名。その他は空 (表示名は呼び出し側が付ける)。 */
  groupName: string
  /**
   * 過去実績 (48 コマ) を日付 (`formatProtoDate` の `YYYY/MM/DD`) で引く表。ページの過去実績と
   * index ではなく日付で対応させる (ページは対象日以降の日を飛ばして返すため、並びが
   * 日付チップと一致しない)。
   */
  pastActualsByDate: ReadonlyMap<string, KomaSlots>
}

/**
 * 内訳欄の表示状態。
 * - `hidden`: 何も出さない (想定モードを公開していない組織、対象が小売でない、
 *   backend より先に frontend を出した時の Unimplemented、直前の成功が hidden の後の失敗)
 * - `loading`: 読み込み中 (想定モードを公開していない組織で一瞬でも何かを出さないよう、何も出さない)
 * - `error`: 内訳欄にだけエラーを出す (ページ本体は使える)
 * - `direct`: 「小売で想定」(グループ未作成ならその旨)。タブは出さない
 * - `byGroup`: 「グループ別」と、合計 + 各グループ (グループ名の昇順) + その他のタブ
 */
export type RetailerBreakdownView =
  | { kind: 'hidden' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'direct'; groupMissing: boolean }
  | { kind: 'byGroup'; leaves: BreakdownLeafView[] }

/** `RetailerBreakdownDirect.status` → グループ未作成か。 */
const GROUP_MISSING_BY_STATUS: Record<DemandBreakdownStatus, boolean> = {
  [DemandBreakdownStatus.UNSPECIFIED]: false,
  [DemandBreakdownStatus.DEFAULT]: false,
  [DemandBreakdownStatus.DIRECT]: false,
  [DemandBreakdownStatus.BY_GROUP]: false,
  [DemandBreakdownStatus.BY_GROUP_NO_GROUPS]: true,
}

/**
 * 応答の oneof を表示状態へ写す。oneof が空 (この版が知らない状態) は画面を変えない側
 * (`hidden`) に倒す。key の無い葉 (同) はタブにしない。
 */
export function toRetailerBreakdownView(
  response: GetDemandForecastRetailerBreakdownResponse,
): RetailerBreakdownView {
  const { breakdown } = response
  switch (breakdown.case) {
    case 'hidden':
    case undefined:
      return { kind: 'hidden' }
    case 'direct':
      return {
        kind: 'direct',
        groupMissing: GROUP_MISSING_BY_STATUS[breakdown.value.status],
      }
    case 'byGroup':
      return {
        kind: 'byGroup',
        leaves: breakdown.value.leaves.flatMap((leaf): BreakdownLeafView[] => {
          const pastActualsByDate = new Map(
            leaf.pastActuals.map((pa) => [
              formatProtoDate(pa.date),
              protoSlotsToKoma(pa.slots),
            ]),
          )
          switch (leaf.key.case) {
            case 'demandGroupId':
              return [
                {
                  key: `group:${leaf.key.value}`,
                  kind: 'group',
                  groupName: leaf.groupName,
                  pastActualsByDate,
                },
              ]
            case 'residual':
              return [
                {
                  key: 'residual',
                  kind: 'residual',
                  groupName: '',
                  pastActualsByDate,
                },
              ]
            case undefined:
              return []
          }
        }),
      }
  }
}

/**
 * 入力対象の葉は page の下書きから作る。内訳 RPC は過去実績だけを補うため、失敗や古い
 * 応答で入力タブ・編集対象を変えない。
 */
export function breakdownLeavesFromPage(
  retailer: RetailerDraft | undefined,
): BreakdownLeafView[] | undefined {
  if (retailer?.byGroup === undefined) return undefined
  return retailer.byGroup.leaves.flatMap((leaf): BreakdownLeafView[] => {
    switch (leaf.key.case) {
      case 'demandGroupId':
        return [
          {
            key: `group:${leaf.key.value}`,
            kind: 'group',
            groupName: leaf.groupName,
            pastActualsByDate: new Map(),
          },
        ]
      case 'residual':
        return [
          {
            key: 'residual',
            kind: 'residual',
            groupName: '',
            pastActualsByDate: new Map(),
          },
        ]
      case undefined:
        return []
    }
  })
}

/** page 所有の葉に、成功した同じ key の内訳実績だけを結合する。 */
export function joinBreakdownActuals(
  leaves: readonly BreakdownLeafView[],
  actuals: RetailerBreakdownView,
): BreakdownLeafView[] {
  if (actuals.kind !== 'byGroup') return [...leaves]
  const actualsByKey = new Map(actuals.leaves.map((leaf) => [leaf.key, leaf]))
  return leaves.map((leaf) => ({
    ...leaf,
    pastActualsByDate:
      actualsByKey.get(leaf.key)?.pastActualsByDate ?? new Map(),
  }))
}

/**
 * 選んだタブの葉を返す。合計タブ、または選んだ葉が今の内訳に無い時 (内訳の読み直しの失敗・
 * 一括の小売に変わった等) は undefined (= 合計として扱う)。
 */
export function selectBreakdownLeaf(
  view: RetailerBreakdownView,
  tabKey: string,
): BreakdownLeafView | undefined {
  if (view.kind !== 'byGroup' || tabKey === BREAKDOWN_TOTAL_TAB)
    return undefined
  return view.leaves.find((leaf) => leaf.key === tabKey)
}
